import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { resolveBaseUrl } from './transferClient';
import { getAuthToken } from 'utils/auth';

const extractTargetSystem = (args: FetchArgs): string => {
    const body = args.body as Record<string, unknown> | undefined;
    const params = args.params as Record<string, unknown> | undefined;
    return ((body?.target_system ?? params?.target_system) as string | undefined) ?? '';
};

const stripTargetSystem = (args: FetchArgs): FetchArgs => {
    const params = args.params as Record<string, unknown> | undefined;
    const { target_system: _p, ...cleanParams } = params ?? {};
    return {
        ...args,
        ...(params ? { params: cleanParams } : {}),
    };
};

const dynamicBaseQuery: BaseQueryFn<FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
    const targetSystem = extractTargetSystem(args);
    const baseUrl = await resolveBaseUrl(targetSystem, api.dispatch);
    const token = await getAuthToken();

    return fetchBaseQuery({
        baseUrl,
        prepareHeaders: headers => {
            if (token) headers.set('Authorization', `Bearer ${token}`);
            return headers;
        },
    })(stripTargetSystem(args), api, extraOptions);
};

export const transferApi = createApi({
    reducerPath: 'transferReducer',
    baseQuery: dynamicBaseQuery,
    endpoints: build => ({
        fetchFileContent: build.query<
            string,
            { dataset_id: string; path: string; result_name?: string; offset?: number; target_system?: string }
        >({
            query: args => ({
                url: `/zip`,
                params: {
                    dataset_id: args.dataset_id,
                    path: args.path,
                    result_name: args.result_name,
                    offset: args.offset,
                    target_system: args.target_system,
                },
                responseHandler: async (response: Response) => {
                    if (response.ok) {
                        const blob = await response.blob();
                        return new Promise<string>((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onload = () => resolve(reader.result as string);
                            reader.onerror = () => reject(reader.error);
                            reader.readAsText(blob);
                        });
                    }
                    throw new Error(await response.text());
                },
            }),
        }),

        fetchImageContent: build.query<
            Blob,
            { dataset_id: string; path: string; result_name?: string; offset?: number; target_system?: string }
        >({
            query: args => ({
                url: `/zip`,
                params: {
                    dataset_id: args.dataset_id,
                    path: args.path,
                    result_name: args.result_name,
                    offset: args.offset,
                    target_system: args.target_system,
                },
                responseHandler: async (response: Response) => {
                    if (response.ok) {
                        return await response.blob();
                    }
                    throw new Error(await response.text());
                },
            }),
        }),

        hpcListing: build.mutation<HpcListingResponse, HpcListingRequest>({
            query: body => ({ url: `/hpc/listing`, method: 'POST', body }),
        }),

        hpcTouch: build.mutation<unknown, HpcTouchRequest>({
            query: body => ({ url: `/hpc/touch`, method: 'POST', body }),
        }),

        hpcDelete: build.mutation<string, HpcDeleteRequest>({
            query: body => ({ url: `/hpc/delete`, method: 'POST', body }),
        }),

        hpcMkdir: build.mutation<string, HpcMkdirRequest>({
            query: body => ({ url: `/hpc/mkdir`, method: 'POST', body }),
        }),

        datasetDownload: build.mutation<null, DatasetDownloadRequest>({
            queryFn: async (args, api) => {
                try {
                    const baseUrl = await resolveBaseUrl(args.target_system, api.dispatch);
                    const token = await getAuthToken();

                    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                    if (token) headers.Authorization = `Bearer ${token}`;

                    const response = await fetch(`${baseUrl}/irods/ticket`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({
                            dataset_id: args.dataset_id,
                            path: args.path ?? "",
                            result_name: args.result_name ?? undefined,
                            offset: args.offset ?? 0,
                            compute_checksum: args.compute_checksum ?? false
                        }),
                    });

                    if (!response.ok) throw new Error((await response.text()) || `Ticket failed: ${response.status}`);

                    const data = (await response.json()) as { ticket: string };
                    if (!data.ticket) throw new Error('No ticket returned');

                    const downloadUrl = `${baseUrl}/zip?ticket=${encodeURIComponent(data.ticket)}`;

                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    requestAnimationFrame(() => document.body.removeChild(link));

                    return { data: null };
                } catch (e) {
                    return {
                        error: { status: 'CUSTOM_ERROR', error: e instanceof Error ? e.message : 'Download failed' },
                    };
                }
            },
        }),

        hpcDownloadTicket: build.mutation<null, HpcDownloadTicketRequest>({
            queryFn: async (args, api) => {
                try {
                    const baseUrl = await resolveBaseUrl(args.target_system, api.dispatch);
                    const token = await getAuthToken();

                    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                    if (token) headers.Authorization = `Bearer ${token}`;

                    const response = await fetch(`${baseUrl}/hpc/download/ticket`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({
                            target_system: args.target_system,
                            target_resource: args.target_resource,
                            project: args.project,
                            path: args.path,
                        }),
                    });

                    if (!response.ok) throw new Error((await response.text()) || `Ticket failed: ${response.status}`);

                    const data = (await response.json()) as { ticket: string };
                    if (!data.ticket) throw new Error('No ticket returned');

                    const downloadUrl = `${baseUrl}/hpc/download?ticket=${encodeURIComponent(data.ticket)}`;

                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.download = args.filename;
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    link.click();
                    requestAnimationFrame(() => document.body.removeChild(link));

                    return { data: null };
                } catch (e) {
                    return {
                        error: { status: 'CUSTOM_ERROR', error: e instanceof Error ? e.message : 'Download failed' },
                    };
                }
            },
        }),
    }),
});

export const {
    useFetchFileContentQuery,
    useLazyFetchFileContentQuery,
    useFetchImageContentQuery,
    useLazyFetchImageContentQuery,
    useDatasetDownloadMutation,
    useHpcListingMutation,
    useHpcTouchMutation,
    useHpcDeleteMutation,
    useHpcMkdirMutation,
    useHpcDownloadTicketMutation,
} = transferApi;

export type StatusResponse = {
    request_id?: string | null;
    task_id?: string | null;
    type?: string | null;
    user?: string | null;
    created_at?: string | null;
    project?: string | null;
    status?: string | null;
    result: string;
    access?: string | null;
    dataset_id?: string | null;
};

export type StatusResponseList = {
    total: number;
    requests: StatusResponse[];
};

export type HpcListingItem = {
    name: string;
    type: 'file' | 'directory' | string;
    size?: number;
    metadata?: Record<string, unknown>;
};

export type HpcListingResponse = {
    name: string;
    type: string;
    contents: HpcListingItem[];
    size: number;
};

export type HpcListingRequest = {
    target_system: string;
    target_resource: string;
    project: string;
    path: string;
};

export type HpcTouchRequest = {
    path: string;
    target_system: string;
    target_resource: string;
    project: string;
    overwrite?: boolean;
    expand?: boolean;
};

export type HpcDeleteRequest = {
    target_system: string;
    target_resource: string;
    project: string;
    path: string;
};

export type HpcMkdirRequest = {
    target_system: string;
    target_resource: string;
    project: string;
    path: string;
};

export type HpcDownloadTicketRequest = {
    target_system: string;
    target_resource: string;
    project: string;
    path: string;
    filename: string;
};

export type DatasetDownloadRequest = {
    dataset_id: string;
    target_system: string;
    path?: string;
    result_name?: string;
    offset?: number;
    compute_checksum?: boolean;
};